import React, { ForwardedRef, forwardRef, ReactNode } from 'react';
import {
  FormControl,
  FormControlProps,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  SelectProps,
  MenuItemProps,
  SxProps,
  InputProps,
  FormHelperText,
  styled,
} from '@mui/material';
import styles from './Select.module.scss';

export type SelectOptionType = {
  value: any;
  title: string;
  [key: string]: any;
};

type MySelectProps = {
  items: SelectOptionType[];
  renderItem?: (item: SelectOptionType) => ReactNode;
  inputProps?: InputProps;
  containerSx?: SxProps;
  readOnly?: boolean;
  helperText?: string;
  itemProps?: MenuItemProps;
};

const CustomSelect = styled(MuiSelect)(({ theme, error, color }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  borderRadius: 8,
  position: 'relative',
  border: '1px solid #ced4da',
  width: 'auto',
  padding: '6px 10px',
  transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
  '&:focus-within': {
    borderColor: (() => {
      if (error) {
        return theme.palette.error.main;
      }
      if (color) {
        return theme.palette[color]?.main;
      }
      return theme.palette.primary.main;
    })(),
  },
  '&:before': {
    display: 'none',
  },
  '&:after': {
    display: 'none',
  },
  '.MuiSelect-select': {
    padding: 0,
    background: 'transparent',
  },
}));

export const Select = forwardRef<HTMLDivElement, SelectProps & MySelectProps & FormControlProps>(
  (props: MySelectProps & SelectProps & FormControlProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      items,
      label,
      inputProps,
      readOnly,
      itemProps,
      containerSx,
      disabled,
      helperText,
      fullWidth,
      error,
      children,
      renderItem,
      size,
      ...rest
    } = props;
    return (
      <FormControl
        sx={containerSx}
        fullWidth={fullWidth}
        size={size}
        variant="standard"
        error={error}
        disabled={disabled}
      >
        {label ? (
          <InputLabel className={styles.label} shrink>
            {label}
          </InputLabel>
        ) : null}
        <CustomSelect ref={ref} inputProps={{ ...inputProps, readOnly }} {...rest}>
          {readOnly ? (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          ) : null}
          {!!items &&
            items.map(
              (item) =>
                renderItem?.(item) || (
                  <MenuItem key={item.value} value={item.value} {...itemProps}>
                    {item.title}
                  </MenuItem>
                ),
            )}
          {children}
        </CustomSelect>
        {!!helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    );
  },
);
